/**
 * CaptaZap — resolucao das variaveis de template pelo CRM.
 * V5.4: dados de consulta precisam pertencer ao lead selecionado; nao ha
 * substituicao por exemplos, operador, mensagem de chat ou contato homonimo.
 */
export const META_TEMPLATE_VARIABLE_GROUPS = [
  { id: "cliente", label: "Cliente / paciente", icon: "👤", items: [
    { key: "cliente.nome", label: "Nome completo", example: "Nome cadastrado do paciente", hint: "Nome do paciente no CRM" },
    { key: "cliente.primeiro_nome", label: "Primeiro nome", example: "Primeiro nome cadastrado", hint: "Primeiro nome do paciente" },
    { key: "cliente.telefone", label: "Telefone", example: "Telefone do cadastro", hint: "Celular do contato" },
  ] },
  { id: "agendamento", label: "Agendamento", icon: "📅", items: [
    { key: "agendamento.data", label: "Data da consulta", example: "Consultar agenda do paciente", hint: "Data do agendamento real" },
    { key: "agendamento.hora", label: "Horário da consulta", example: "Consultar agenda do paciente", hint: "Hora do agendamento real" },
    { key: "agendamento.data_hora", label: "Data e horário", example: "Consultar agenda do paciente", hint: "Data e hora reais do paciente" },
    { key: "agendamento.profissional", label: "Profissional / dentista", example: "Consultar dentista do paciente", hint: "Profissional do agendamento ou da lead vinculada" },
    { key: "agendamento.procedimento", label: "Procedimento", example: "Consultar procedimento agendado", hint: "Procedimento vinculado" },
  ] },
  { id: "empresa", label: "Empresa / clínica", icon: "🏥", items: [
    { key: "empresa.nome", label: "Nome da empresa", example: "Nome fantasia", hint: "Nome fantasia" },
    { key: "empresa.endereco", label: "Endereço", example: "Endereço cadastrado", hint: "Endereço da unidade" },
    { key: "empresa.telefone", label: "Telefone da empresa", example: "Telefone comercial", hint: "Contato comercial" },
  ] },
  { id: "atendimento", label: "Atendimento", icon: "💬", items: [
    { key: "atendimento.nome", label: "Nome do atendente", example: "Atendente logado", hint: "Operador logado" },
    { key: "atendimento.protocolo", label: "Número do chamado", example: "Número do chamado", hint: "Identificador do atendimento" },
  ] },
];
export const META_TEMPLATE_VARIABLES = META_TEMPLATE_VARIABLE_GROUPS.flatMap(group => group.items);
export const META_TEMPLATE_VARIABLE_BY_KEY = Object.fromEntries(META_TEMPLATE_VARIABLES.map(item => [item.key, item]));

const first = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || typeof value === "object") continue;
    const text = String(value).trim();
    if (text && !["null", "undefined", "0"].includes(text.toLowerCase())) return text;
  }
  return "";
};
const normId = value => first(value);
const ownId = obj => normId(first(obj?.id, obj?.lead_id, obj?.id_lead, obj?.leadId));
const relatedId = obj => normId(first(
  obj?.lead_contexto_id, obj?.contexto_lead_id, obj?.lead_id, obj?.id_lead,
  obj?.leadId, obj?.lead?.id, obj?.lead?.lead_id, obj?.lead?.id_lead,
));
const cleanStatus = value => first(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function nomePessoaValidoMeta(value) {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text || text.length > 110 || text.length < 2) return "";
  if (/\.(?:pdf|jpe?g|png|webp|gif|mp4|docx?|xlsx?|zip|rar|webm|ogg)(?:\?|$)/i.test(text)) return "";
  if (/^(?:contato|desconhecido|sem nome|cliente|whatsapp|paciente|anexo|arquivo)$/i.test(text)) return "";
  if (/^[A-F\d]{20,}$/i.test(text) || /\d{4,}/.test(text)) return "";
  if (/[<>@/\\]|https?:|\{\{|\}\}/i.test(text)) return "";
  if (!/^[\p{L}\p{M}][\p{L}\p{M}\s.'-]+$/u.test(text)) return "";
  return text;
}

// Usa ano/mes/dia de ISO ou DATETIME do CRM sem inventar dia a partir do fuso UTC.
function appointmentDate(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const v = value.toISOString();
    return appointmentDate(v);
  }
  const s = first(value); if (!s) return null;
  let parts;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (iso) parts = [+iso[1],+iso[2],+iso[3]];
  else if (br) parts = [+br[3],+br[2],+br[1]];
  else return null;
  const [year,month,day]=parts;
  const date = new Date(year,month-1,day);
  return date.getFullYear()===year && date.getMonth()===month-1 && date.getDate()===day ? date.getTime() : null;
}
const dateRaw = item => first(
  item?.data_agendamento, item?.dataAgendamento, item?.data_consulta, item?.data_marcada,
  item?.agendado_para, item?.data, item?.inicio, item?.start, item?.start_at,
  item?.datahora, item?.data_hora, item?.dataHora,
);
const hourRaw = item => first(
  item?.hora_agendamento, item?.horaAgendamento, item?.hora_consulta,
  item?.hora_marcada, item?.horario_agendamento, item?.hora_inicio,
  item?.hora, item?.horario, item?.time,
);
const disabled = item => /cancel|exclu|desmarc|faltou|nao.comparec|inativ|comparec|atendid|concluid|finaliz|removid/i.test(
  cleanStatus(first(item?.status_agendamento,item?.statusAgendamento,item?.status)),
);
const pickDate = item => appointmentDate(dateRaw(item));
function belongs(item, leadId, companyId, embedded) {
  if (!item || typeof item !== "object" || disabled(item)) return false;
  const aCompany = normId(first(item?.empresa_id,item?.empresaId));
  if (aCompany && companyId && aCompany !== companyId) return false;
  const aLead = relatedId(item);
  if (aLead && leadId && aLead !== leadId) return false;
  // Listas globais so podem ser usadas quando a lead do agendamento coincide.
  if (!embedded && (!leadId || aLead !== leadId)) return false;
  // Se nao houver lead vinculada ao chamado, nao adivinhar pela conversa/anexo.
  if (!leadId) return false;
  return pickDate(item) !== null;
}
function getSelectedLead(conversa, leadId) {
  const c=conversa||{};
  const possible=[c.lead_contexto,c.leadContexto,c.lead];
  // O ChatPanel fornece a lead selecionada como conversa.lead.
  return possible.find(lead => lead && typeof lead==="object" && ownId(lead)===leadId) || null;
}
/**
 * Restringe por lead + empresa e escolhe a consulta futura mais proxima.
 * Retorna null quando a agenda nao confirma o paciente/consulta.
 */
export function resolverContextoMetaAgendamento({conversa,agendamentos,leadContextoId,empresaId,agora}={}) {
  const c=conversa||{};
  const leadId=normId(leadContextoId)||relatedId(c);
  const companyId=normId(empresaId)||normId(c.empresa_id);
  if (!leadId) return null;
  const lead=getSelectedLead(c,leadId);
  const direct=[c.agendamento,c.ultimo_agendamento,c._ultimo_agendamento,
    lead?.agendamento,lead?.ultimo_agendamento,lead?._ultimo_agendamento];
  const local=[c.agendamentos,c.agendamentosLead,lead?.agendamentos,lead?.agendamentosLead];
  // Dados embutidos na conversa so podem ser utilizados com referencia consistente.
  const candidates=[...direct,...local.flatMap(v=>Array.isArray(v)?v:[])].filter(item=>belongs(item,leadId,companyId,true));
  candidates.push(...(Array.isArray(agendamentos)?agendamentos:[]).filter(item=>belongs(item,leadId,companyId,false)));
  const unique=[...new Map(candidates.map(item => [first(item?.id,item?.agendamento_id)||`${dateRaw(item)}:${hourRaw(item)}:${relatedId(item)}`,item])).values()];
  const now=Number.isFinite(Number(agora))?Number(agora):Date.now();
  const today=new Date(now); today.setHours(0,0,0,0);
  return unique.filter(item=>pickDate(item)>=today.getTime()).sort((a,b)=>pickDate(a)-pickDate(b))[0]||null;
}
const formatDate = raw => {
  const timestamp=appointmentDate(raw); if(timestamp===null)return "";
  const d=new Date(timestamp);return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
};
function formatTime(item) {
  let s=hourRaw(item);
  if(!s) {
    const d=dateRaw(item);
    // ISO 'T00:00:00.000Z' de coluna DATE nao comprova o horario da consulta.
    const m=/(?:T|\s)(\d{1,2}:\d{2})(?::\d{2})?/.exec(d);
    if(m && (m[1]!=="00:00" || !/Z$/i.test(d))) s=m[1];
  }
  // HH:MM ou HHhMM.
  const time=/^(\d{1,2})(?:[:h](\d{2}))?(?::\d{2})?$/.exec(s);
  if(!time)return "";
  const h=+time[1],min=+(time[2]||0);
  if(h>23||min>59)return "";
  return `${h}h${min===0?"":String(min).padStart(2,"0")}`;
}
function personFromDentist(id, dentistas=[]) {
  const needle=normId(id);if(!needle)return "";
  const entry=(Array.isArray(dentistas)?dentistas:[]).find(item=>
    [item?.id,item?.id_dentista,item?.dentista_id,item?.id_profissional,item?.profissional_id].some(v=>normId(v)===needle),
  );
  return entry?nomePessoaValidoMeta(first(entry.profissional,entry.nome,entry.nome_profissional,entry.nome_dentista)):"";
}
function dentistFromAppointment(appointment, dentistas, conversa, leadId) {
  if(!appointment)return "";
  const embeddedId=first(appointment?.id_dentista,appointment?.dentista_id,
    appointment?.id_profissional,appointment?.profissional_id,
    typeof appointment?.dentista==="object"?appointment.dentista.id:"",
    typeof appointment?.profissional==="object"?appointment.profissional.id:"");
  const embeddedName=first(appointment?.profissional_nome,appointment?.nome_profissional,
    appointment?.dentista_nome,appointment?.nome_dentista,
    typeof appointment?.dentista==="object"?appointment.dentista.nome:appointment?.dentista,
    typeof appointment?.profissional==="object"?appointment.profissional.nome:appointment?.profissional);
  // O agendamento pode ter o dentista diretamente; essa informacao prevalece.
  const byAppointment=personFromDentist(embeddedId,dentistas)||nomePessoaValidoMeta(embeddedName);
  if(byAppointment)return byAppointment;
  // Na modelagem do CaptaCRM o profissional tambem e registrado na LEAD.
  // So consultamos essa fallback quando o agendamento foi confirmado para ela.
  const lead=getSelectedLead(conversa,leadId);
  if(!lead || (relatedId(appointment) && relatedId(appointment)!==leadId))return "";
  const id=first(lead.id_dentista,lead.dentista_id,lead.id_profissional,lead.profissional_id);
  return personFromDentist(id,dentistas)||nomePessoaValidoMeta(first(
    lead.dentista_nome,lead.nome_dentista,lead.profissional_nome,lead.nome_profissional,
  ));
}
export function diagnosticarContextoMetaAgendamento({conversa,agendamentos,dentistas,leadContextoId,empresaId,agora}={}) {
  const c=conversa||{};
  const leadId=normId(leadContextoId)||relatedId(c);
  const matching=(Array.isArray(agendamentos)?agendamentos:[]).filter(item =>
    relatedId(item)===leadId && (!first(item.empresa_id,item.empresaId) || first(item.empresa_id,item.empresaId)===String(empresaId)),
  );
  const appointment=resolverContextoMetaAgendamento({conversa:c,agendamentos,leadContextoId,empresaId,agora});
  const date=appointment?formatDate(dateRaw(appointment)):"";
  const time=appointment?formatTime(appointment):"";
  const dentist=appointment?dentistFromAppointment(appointment,dentistas,c,leadId):"";
  const status=!leadId?"sem_lead":!appointment?(matching.length?"sem_agenda_futura":"sem_agendamento"):(!time||!dentist?"incompleto":"completo");
  return {status,leadId,agendamentoId:appointment?first(appointment.id,appointment.agendamento_id):"",date,time,dentist,totalCorrespondentes:matching.length};
}
/** Ausencia de dados confiaveis => sugestao vazia; exemplos nunca sao enviados. */
export function resolverSugestaoVariavelMeta(key,{conversa,empresaNome,operadorNome,telefone,dentistas,agendamentos,leadContextoId,empresaId,agora}={}) {
  const c=conversa||{};
  const leadId=normId(leadContextoId)||relatedId(c);
  const lead=getSelectedLead(c,leadId)||c.lead_contexto||c.leadContexto||c.lead||{};
  const appointment=resolverContextoMetaAgendamento({conversa:c,agendamentos,leadContextoId,empresaId,agora});
  const name=[lead.nome_lead,lead.nomeLead,lead.nome,c.nome_oficial,c.nomeOficial,c.nome_contato,c.nomeContato,c.nome,c.nome_whatsapp].map(nomePessoaValidoMeta).find(Boolean)||"";
  const date=appointment?formatDate(dateRaw(appointment)):"";
  const time=appointment?formatTime(appointment):"";
  const map={
    "cliente.nome":name,
    "cliente.primeiro_nome":name?name.split(" ")[0]:"",
    "cliente.telefone":first(telefone,c.telefone,c.numero),
    "agendamento.data":date,
    "agendamento.hora":time,
    "agendamento.data_hora":date&&time?`${date} às ${time}`:"",
    "agendamento.profissional":dentistFromAppointment(appointment,dentistas,c,leadId),
    "agendamento.procedimento":first(appointment?.procedimento_nome,appointment?.nome_procedimento,
      typeof appointment?.procedimento==="object"?appointment.procedimento.nome:appointment?.procedimento),
    "empresa.nome":first(empresaNome,c.empresa_nome,c.empresaNome),
    "empresa.endereco":first(c.endereco_unidade,c.empresa_endereco),
    "empresa.telefone":first(c.empresa_telefone),
    "atendimento.nome":nomePessoaValidoMeta(operadorNome),
    "atendimento.protocolo":first(c.protocolo,c.protocolo_id,c.chamado_id,c.id_chamado),
  };
  return map[key]||"";
}
